import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Mail, Users, Send, Calendar, AlertCircle, CheckCircle, XCircle, Edit, Trash2 } from 'lucide-react';
import emailCampaignService from '../../services/emailCampaignService';

const EmailCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [recipientFilter, setRecipientFilter] = useState('all');

  useEffect(() => {
    loadCampaignData();
  }, [id]);

  const loadCampaignData = async () => {
    try {
      setLoading(true);
      const [campaignData, recipientsData] = await Promise.all([
        emailCampaignService.getCampaign(id),
        emailCampaignService.getCampaignRecipients(id)
      ]);
      
      setCampaign(campaignData);
      setRecipients(Array.isArray(recipientsData) ? recipientsData : recipientsData.results || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load campaign:', err);
      setError('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrepareCampaign = async () => {
    try {
      setSending(true);
      const result = await emailCampaignService.prepareCampaign(id);
      alert(`Campaign prepared! ${result.recipient_count} recipients added.`);
      loadCampaignData();
    } catch (err) {
      console.error('Prepare failed:', err);
      alert(err.response?.data?.error || 'Failed to prepare campaign');
    } finally {
      setSending(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!window.confirm('Are you sure you want to send this campaign? This action cannot be undone.')) {
      return;
    }

    try {
      setSending(true);
      const result = await emailCampaignService.sendCampaign(id);
      alert(`Campaign sent! ${result.sent_count} emails sent, ${result.failed_count} failed.`);
      loadCampaignData();
    } catch (err) {
      console.error('Send failed:', err);
      alert(err.response?.data?.error || 'Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return;
    }

    try {
      await emailCampaignService.deleteCampaign(id);
      navigate('/admin/email-campaigns/campaigns');
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete campaign');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      paused: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-600',
      failed: 'bg-red-100 text-red-600',
      bounced: 'bg-orange-100 text-orange-600',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredRecipients = recipients.filter(r => 
    recipientFilter === 'all' || r.status === recipientFilter
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error || 'Campaign not found'}</p>
          <Link to="/admin/email-campaigns/campaigns" className="mt-4 btn-primary inline-block">
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{campaign.name}</h1>
              <p className="mt-2 text-gray-600">{campaign.description || 'No description'}</p>
            </div>
            <div className="flex space-x-3">
              {campaign.is_editable && (
                <Link
                  to={`/admin/email-campaigns/campaigns/${id}/edit`}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  <Edit className="h-5 w-5" />
                  <span>Edit</span>
                </Link>
              )}
              <button
                onClick={handleDelete}
                className="btn-secondary text-red-600 hover:bg-red-50 inline-flex items-center space-x-2"
              >
                <Trash2 className="h-5 w-5" />
                <span>Delete</span>
              </button>
              <Link
                to="/admin/email-campaigns/campaigns"
                className="btn-secondary"
              >
                Back to List
              </Link>
            </div>
          </div>
        </div>

        {/* Status and Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <span className={`mt-2 inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(campaign.status)}`}>
                  {campaign.status}
                </span>
              </div>
              <Mail className="h-10 w-10 text-gray-400" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Recipients</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{campaign.total_recipients}</p>
              </div>
              <Users className="h-10 w-10 text-primary-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Emails Sent</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{campaign.emails_sent}</p>
              </div>
              <Send className="h-10 w-10 text-green-600" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Success Rate</p>
                <p className="text-3xl font-bold text-blue-600 mt-2">
                  {campaign.success_rate ? `${campaign.success_rate.toFixed(1)}%` : 'N/A'}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Campaign Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Campaign Details</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-600">Template</dt>
                <dd className="text-sm text-gray-900 mt-1">{campaign.template_name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-600">Created</dt>
                <dd className="text-sm text-gray-900 mt-1">{formatDate(campaign.created_at)}</dd>
              </div>
              {campaign.scheduled_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-600">Scheduled</dt>
                  <dd className="text-sm text-gray-900 mt-1">{formatDate(campaign.scheduled_at)}</dd>
                </div>
              )}
              {campaign.sent_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-600">Sent</dt>
                  <dd className="text-sm text-gray-900 mt-1">{formatDate(campaign.sent_at)}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-600">Created By</dt>
                <dd className="text-sm text-gray-900 mt-1">{campaign.created_by_email || 'N/A'}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Filter Criteria</h2>
            {campaign.filter_criteria && Object.keys(campaign.filter_criteria).length > 0 ? (
              <dl className="space-y-2">
                {Object.entries(campaign.filter_criteria).map(([key, value]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <dt className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</dt>
                    <dd className="text-gray-900 font-medium">
                      {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-gray-600">No filters applied (all users)</p>
            )}
          </div>
        </div>

        {/* Send Campaign Button */}
        {campaign.status === 'draft' && campaign.total_recipients > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-start space-x-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Ready to Send</h3>
                <p className="text-yellow-800 mb-4">
                  This campaign is prepared with {campaign.total_recipients} recipients. 
                  Click the button below to send the campaign.
                </p>
                <button
                  onClick={handleSendCampaign}
                  disabled={sending}
                  className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                >
                  <Send className="h-5 w-5" />
                  <span>{sending ? 'Sending...' : 'Send Campaign Now'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recipients List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Recipients</h2>
            <select
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All ({recipients.length})</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="bounced">Bounced</option>
            </select>
          </div>

          {filteredRecipients.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No recipients yet</p>
              <p className="text-sm text-gray-500 mb-6">
                Prepare the campaign to filter users and create recipient list
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={handlePrepareCampaign}
                  disabled={sending}
                  className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
                >
                  <Users className="h-5 w-5" />
                  <span>{sending ? 'Preparing...' : 'Prepare Campaign'}</span>
                </button>
                {campaign.is_editable && (
                  <Link
                    to={`/admin/email-campaigns/campaigns/${id}/edit`}
                    className="btn-secondary inline-flex items-center space-x-2"
                  >
                    <span>Edit Filters</span>
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{recipient.user_email}</div>
                        {recipient.user_name && (
                          <div className="text-sm text-gray-500">{recipient.user_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(recipient.status)}`}>
                          {recipient.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(recipient.sent_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-red-600">
                        {recipient.error_message || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailCampaignDetail;
